import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Skeleton, Stack, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import fp from "lodash/fp"
import get from "lodash/get"

import type { EntityRef } from "types/common.types"
import type { ContextDevice } from "types/device.types"

import { ENTITY_VISUALS } from "helpers/config/entityVisuals"
import { getDevicesLabel } from "helpers/utils/devices"
import EntityLink from "widgets/common/EntityLink"
import PowerLineColumns, { type PowerLineRef } from "widgets/common/PowerLineColumns"
import DevicesCell from "./DevicesCell"
import { getPowerLinesLabel } from "./helpers"

const STRIPE_WIDTH = "2px"
// Two names, their status and the chip need more room than one line or two towers.
const DEVICES_GROW = 1.5
const BLOCK_MAX_WIDTH = "960px"

interface AssetContextBlockProps {
  powerLines?: PowerLineRef[]
  location?: { label: string; refs: EntityRef[] } | null
  devices?: {
    items: ContextDevice[]
    /** Set on a device page: the items are the other devices on that asset. */
    siblingsOf?: "span" | "tower"
  } | null
  /** A cell still loading shows a placeholder in its place, so the others do not wait for it. */
  loading?: {
    powerLines?: boolean
    /** The lines show as soon as they load, with a placeholder where their spans go. */
    spans?: boolean
    location?: boolean
    devices?: boolean
  }
}

interface CellProps {
  label: ReactNode
  children: ReactNode
  stripeColor: string
  /** Stacked on mobile, every cell but the first has a hairline above it. */
  isFirst: boolean
  /** How much of the row it takes next to the others. */
  grow?: number
}

const Cell = ({ label, children, stripeColor, isFirst, grow = 1 }: CellProps) => (
  <Stack
    gap={0.5}
    flex={`${grow} 1 0`}
    minWidth={0}
    paddingX={1.5}
    paddingY={1}
    sx={(theme) => ({
      borderLeft: `${STRIPE_WIDTH} solid ${stripeColor}`,
      ...(!isFirst && {
        borderTop: { xs: `1px solid ${theme.palette.neutral[400]}`, md: "none" },
      }),
    })}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    {children}
  </Stack>
)

interface CellContent {
  key: string
  label: ReactNode
  content: ReactNode
  stripeColor?: string
  grow?: number
}

// Shaped like a label and its first line.
const placeholder = (key: string, grow?: number): CellContent => ({
  key,
  label: <Skeleton width={72} />,
  content: <Skeleton variant="rounded" width={160} height={24} />,
  grow,
})

const AssetContextBlock = ({
  powerLines = [],
  location,
  devices,
  loading = {},
}: AssetContextBlockProps) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const deviceColor = get(theme.palette, ENTITY_VISUALS.device.colorPath) as string

  const getPowerLineCell = (): CellContent | null => {
    if (loading.powerLines) return placeholder("powerline")
    if (powerLines.length === 0) return null
    const withSpans = powerLines.some((powerLine) => powerLine.spans)
    return {
      key: "powerline",
      label: getPowerLinesLabel(powerLines.length, withSpans, t),
      content: <PowerLineColumns lines={powerLines} spansLoading={loading.spans} />,
      // Two columns, or a line over its spans, need the same room as the devices.
      grow: withSpans || powerLines.length > 1 ? DEVICES_GROW : undefined,
    }
  }

  const getLocationCell = (): CellContent | null => {
    if (loading.location) return placeholder("location")
    if (!location || location.refs.length === 0) return null
    return {
      key: "location",
      label: location.label,
      content: (
        <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={0.5}>
          {location.refs.map((ref) => (
            <EntityLink
              key={`${ref.entity}-${ref.id}`}
              entity={ref.entity}
              id={ref.id}
              name={ref.name}
            />
          ))}
        </Stack>
      ),
    }
  }

  const getDevicesCell = (): CellContent | null => {
    if (loading.devices) return placeholder("devices", DEVICES_GROW)
    if (!devices) return null
    const count = devices.items.length
    return {
      key: "devices",
      label: getDevicesLabel(count, devices.siblingsOf, t),
      content: <DevicesCell devices={devices.items} siblingsOf={devices.siblingsOf} />,
      stripeColor: count > 0 ? deviceColor : undefined,
      grow: DEVICES_GROW,
    }
  }

  const cells = fp.compact([getPowerLineCell(), getLocationCell(), getDevicesCell()])

  if (cells.length === 0) return null

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      data-testid="asset-context-block"
      sx={{
        // Full width on mobile. On desktop three cells read best without stretching across the page.
        maxWidth: { md: BLOCK_MAX_WIDTH },
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.neutral[400]}`,
        // The cells' stripes are the left edge, a border there would double them.
        borderLeft: "none",
        borderRadius: "8px",
        // Keeps the stripes inside the rounded corners.
        overflow: "hidden",
      }}
    >
      {cells.map((cell, index) => (
        <Cell
          key={cell.key}
          label={cell.label}
          stripeColor={cell.stripeColor ?? theme.palette.neutral[400]}
          isFirst={index === 0}
          grow={cell.grow}
        >
          {cell.content}
        </Cell>
      ))}
    </Stack>
  )
}

export default AssetContextBlock
